import '@breward/ui/styles/fonts.css';
import i18n from '@breward/i18n';
import { StoreProvider } from '@breward/store';
import { isUnauthorizedError } from '@brewardapp/api-client';
import * as Sentry from '@sentry/react';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App';
import AppErrorBoundary from './components/ErrorBoundary/AppErrorBoundary';
import RouterWithHistory from './utils/router';

let dehydratedState: unknown;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 10,
      staleTime: 30 * 1000,
      retryDelay: 2000,
      // Never retry a 401. With the default 3 retries x 2s delay a rejected
      // request could land its final failure seconds after the user has already
      // signed in successfully, tearing down the fresh session.
      retry: (failureCount: number, error: unknown) =>
        !isUnauthorizedError(error) && failureCount < 3,
    },
  },
});

Sentry.init({
  enabled: !import.meta.env.DEV,
  environment: import.meta.env.VITE_ENVIRONMENT,
  dsn: 'https://d8f0d937a483f9c87764dc3220a440e4@o4506508927172608.ingest.us.sentry.io/4511495290290176',
  integrations: [
    Sentry.browserTracingIntegration({
      enableHTTPTimings: true,
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration()
  ],
  release: `participant-web@${import.meta.env.APP_VERSION}`,
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/.*breward\..*/,
    ...(import.meta.env.VITE_API_HOST ? [import.meta.env.VITE_API_HOST] : []),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <RouterWithHistory>
        <StoreProvider>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={dehydratedState}>
              <I18nextProvider i18n={i18n}>
                <App />
              </I18nextProvider>
            </HydrationBoundary>
            {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
          </QueryClientProvider>
        </StoreProvider>
      </RouterWithHistory>
    </AppErrorBoundary>
  </React.StrictMode>,
);
